// "Replay do dia de trabalho" (DIFERENTE do "Replay do dia", que reproduz o trajeto de visitas).
// Aqui mostramos INÍCIO e FIM do expediente de cada dia (hora + local), a partir da coleção
// trackingLog. Acesso: SÓ acesso total (a rota usa PrivateRoute access="full" = isFullAccess); o item
// de menu aparece só no adminNav. Localização exibida como PINO + coordenada (decisão #4 = sem
// reverse-geocode enquanto o Blaze/Functions não entram).
import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import OsmTiles from '../../components/OsmTiles';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { User, sellerDisplayName } from '../../types';
import { useAccessScope } from '../../hooks/useAccessScope';
import 'leaflet/dist/leaflet.css';

type WorkdayLog = {
  id: string;
  startDate: string;
  startTime?: string;
  startLocation?: { lat: number; lng: number };
  endTime?: string;
  endLocation?: { lat: number; lng: number };
  endedReason?: string;
};

// Data LOCAL (não toISOString/UTC): o startDate é gravado em data local do aparelho; usar UTC aqui
// adiantaria o dia à noite no Brasil (UTC-3) e poderia esconder o dia mais antigo da faixa padrão.
const p2 = (n: number) => String(n).padStart(2, '0');
const isoNDaysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`; };
const fmtDia = (s: string) => { const [y, m, d] = (s || '').split('-'); return d ? `${d}/${m}/${y}` : s; };
const REASON: Record<string, string> = { window: 'fim da janela', manual: 'encerrado manualmente', logout: 'saiu do app' };

function FitBounds({ pts }: { pts: [number, number][] }) {
  const map = useMap();
  useEffect(() => { if (pts.length) { try { map.fitBounds(pts as any, { padding: [50, 50], maxZoom: 16 }); } catch { /* ignore */ } } }, [pts, map]);
  return null;
}

export default function WorkdayReplayPage() {
  const scope = useAccessScope();
  const [sellers, setSellers] = useState<User[]>([]);
  const [sellerId, setSellerId] = useState('');
  const [from, setFrom] = useState(isoNDaysAgo(29));
  const [to, setTo] = useState(isoNDaysAgo(0));
  const [logs, setLogs] = useState<WorkdayLog[]>([]);
  const [sel, setSel] = useState<WorkdayLog | null>(null);
  const [loading, setLoading] = useState(false);
  const [carregou, setCarregou] = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'users'), orderBy('name')))
      .then((s) => setSellers(s.docs.map((d) => ({ id: d.id, ...d.data() })) as User[]))
      .catch(() => { /* ignore */ });
  }, []);

  // Acesso total vê todos; (o gestor com escopo nem chega aqui — a rota é access="full").
  const sellerOptions = useMemo(() => {
    if (scope.full) return sellers;
    const ids = new Set(scope.sellerIds || []);
    return sellers.filter((s) => ids.has(s.id));
  }, [sellers, scope]);

  const carregar = async () => {
    if (!sellerId) return;
    setLoading(true); setSel(null);
    try {
      const snap = await getDocs(query(
        collection(db, 'trackingLog'),
        where('uid', '==', sellerId),
        where('startDate', '>=', from),
        where('startDate', '<=', to),
        orderBy('startDate', 'desc'),
      ));
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as WorkdayLog[];
      setLogs(list);
      setSel(list[0] || null);
    } finally { setLoading(false); setCarregou(true); }
  };

  const pins = useMemo(() => {
    const pts: [number, number][] = [];
    if (sel?.startLocation) pts.push([sel.startLocation.lat, sel.startLocation.lng]);
    if (sel?.endLocation) pts.push([sel.endLocation.lat, sel.endLocation.lng]);
    return pts;
  }, [sel]);
  const center: [number, number] = pins[0] || [-15.78, -47.93];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Replay do dia de trabalho</h1>
        <p style={styles.subtitle}>Início e fim de cada dia (horário e local), com histórico por período.</p>
      </div>

      <div style={styles.controls}>
        <select style={styles.input} value={sellerId} onChange={(e) => setSellerId(e.target.value)}>
          <option value="">Selecione o usuário...</option>
          {sellerOptions.map((s) => <option key={s.id} value={s.id}>{sellerDisplayName(s)}</option>)}
        </select>
        <label style={styles.lbl}>De <input type="date" style={styles.input} value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={styles.lbl}>Até <input type="date" style={styles.input} value={to} min={from} onChange={(e) => setTo(e.target.value)} /></label>
        <button style={styles.loadBtn} onClick={carregar} disabled={!sellerId || loading}>{loading ? 'Carregando...' : 'Carregar'}</button>
      </div>

      {!carregou ? (
        <div style={styles.empty}>Selecione o usuário e o período e clique em <strong>Carregar</strong>.</div>
      ) : logs.length === 0 ? (
        <div style={styles.empty}>Nenhum registro nesse período.</div>
      ) : (
        <div style={styles.grid}>
          <div style={styles.list}>
            {logs.map((l) => (
              <button key={l.id} style={{ ...styles.dayRow, ...(sel?.id === l.id ? styles.dayRowOn : {}) }} onClick={() => setSel(l)}>
                <span style={styles.dayDate}>{fmtDia(l.startDate)}</span>
                <span style={styles.dayTimes}>🟢 {l.startTime || '—'} · 🔴 {l.endTime || 'em aberto'}</span>
              </button>
            ))}
          </div>
          <div>
            <div style={styles.mapWrapper}>
              <MapContainer center={center} zoom={14} style={{ height: 460, width: '100%' }}>
                <OsmTiles />
                <FitBounds pts={pins} />
                {sel?.startLocation && (
                  <CircleMarker center={[sel.startLocation.lat, sel.startLocation.lng]} radius={10} pathOptions={{ color: '#fff', weight: 2, fillColor: '#16a34a', fillOpacity: 1 }}>
                    <Tooltip permanent direction="top">Início {sel.startTime || ''}</Tooltip>
                  </CircleMarker>
                )}
                {sel?.endLocation && (
                  <CircleMarker center={[sel.endLocation.lat, sel.endLocation.lng]} radius={10} pathOptions={{ color: '#fff', weight: 2, fillColor: '#dc2626', fillOpacity: 1 }}>
                    <Tooltip permanent direction="top">Fim {sel.endTime || ''}</Tooltip>
                  </CircleMarker>
                )}
              </MapContainer>
            </div>
            {sel && (
              <div style={styles.detail}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{fmtDia(sel.startDate)}</div>
                <div>🟢 Início: <strong>{sel.startTime || '—'}</strong>{sel.startLocation ? ` · ${sel.startLocation.lat.toFixed(5)}, ${sel.startLocation.lng.toFixed(5)}` : ' · sem local'}</div>
                <div>🔴 Fim: <strong>{sel.endTime || 'em aberto'}</strong>{sel.endLocation ? ` · ${sel.endLocation.lat.toFixed(5)}, ${sel.endLocation.lng.toFixed(5)}` : (sel.endTime ? ' · sem local' : '')}{sel.endedReason ? ` (${REASON[sel.endedReason] || sel.endedReason})` : ''}</div>
                {!sel.startLocation && !sel.endLocation && <div style={{ color: '#999', marginTop: 4 }}>Sem localização registrada neste dia.</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  controls: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' },
  lbl: { fontSize: 13, color: '#666', display: 'flex', alignItems: 'center', gap: 6 },
  input: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, background: '#fff' },
  loadBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  empty: { background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', color: '#999', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  grid: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' },
  list: { background: '#fff', borderRadius: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)', maxHeight: 540, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  dayRow: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, padding: '10px 14px', border: 'none', borderBottom: '1px solid #f0f0f0', background: 'transparent', cursor: 'pointer', textAlign: 'left', width: '100%' },
  dayRowOn: { background: '#fff7ed', borderLeft: '3px solid #f5821f' },
  dayDate: { fontWeight: 700, color: '#1a1a2e', fontSize: 14 },
  dayTimes: { fontSize: 12, color: '#666' },
  mapWrapper: { borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 12px rgba(0,0,0,0.08)' },
  detail: { marginTop: 12, background: '#fff', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', fontSize: 14, color: '#444', lineHeight: 1.7 },
};
